import equals from 'ramda/src/equals'
import i18n from 'i18next'

/**
 * @typedef SendParams
 * @type {object}
 * @property {string} url
 * @property {'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH'} method
 * @property {?object} data
 */

/**
 * @typedef ParsedResponse
 * @type {object}
 * @property {number} status
 * @property {Object} json
 * @property {File=} file
 */

/**
 * @param {Response} response
 * @returns {Promise}
 */
const parseResponse = (response) => {
  return new Promise((resolve, reject) =>
    response.json().then(json =>
      resolve({
        status: response.status,
        ok: response.ok,
        json
      })
    ).catch((e) => {
      console.warn(e)
      reject(new Error(`${response.status} ${response.statusText}`)
      )
    })
  )
}
/**
 * @param {Response} response
 * @returns {Promise}
 */
const parseFileResponse = (response) => {
  return new Promise((resolve, reject) =>
    response.blob().then(blob =>
      resolve({
        status: response.status,
        ok: response.ok,
        // eslint-disable-next-line no-undef
        file: new File([blob], 'image', {
          type: blob.type
        })
      })
    ).catch((e) => {
      console.warn(e)
      reject(new Error(`${response.status} ${response.statusText}`)
      )
    })
  )
}
/**
 * @param {function} resolve
 * @param {function} reject
 * @returns {function(...[*]=)}
 */
const resolver = (resolve, reject) =>
  /**
  * @param {ParsedResponse} props
  */
  (props) => {
    const { status, json, file } = props
    if (status >= 400) {
      return reject(json)
    }

    if (file) {
      return resolve(file)
    }

    return resolve(json)
  }

const provider = {
  /**
   * @param {SendParams} props
   */
  send ({ url, method = 'POST', data = {}, parser = parseResponse }) {
    const languageHeader = { 'Accept-Language': i18n.language }
    const requestDefaults = { credentials: 'include' }
    const requestGetParams = {
      method: 'GET',
      headers: { ...languageHeader },
      ...requestDefaults
    }
    const requestPostParams = {
      headers: { ...languageHeader, 'Content-Type': 'application/json' },
      ...requestDefaults
    }

    const body = JSON.stringify(data)
    const params = equals('GET', method) ? requestGetParams : { method, body, ...requestPostParams }

    return new Promise((resolve, reject) =>
      fetch(url, params)
        .then(parser)
        .then(resolver(resolve, reject))
        .catch((error) => reject(error))
    )
  },

  /**
   * @param {string} url
   * @param {Object} data
   */
  post (url, data = {}) {
    return this.send({ url, data, method: 'POST' })
  },

  /**
   * @param {string} url
   */
  get (url) {
    return this.send({ url, method: 'GET' })
  },

  /**
   * @param {string} url
   */
  getImage (url) {
    return this.send({ url, method: 'GET', parser: parseFileResponse })
  },

  /**
   * @param {string} url
   */
  delete (url) {
    return this.send({ url, method: 'DELETE' })
  },

  /**
   * @param {string} url
   * @param {object} formData
   * @returns {Promise}
   */
  postMultipart (url, formData) {
    const languageHeader = { 'Accept-Language': i18n.language }
    const requestDefaults = { credentials: 'include' }
    const params = {
      method: 'POST',
      headers: { ...languageHeader },
      ...requestDefaults,
      body: formData
    }
    return new Promise((resolve, reject) =>
      fetch(url, params)
        .then(parseResponse)
        .then(resolver(resolve, reject))
        .catch((error) => reject(error))
    )
  }
}

export default provider
